import { apiFetch } from "@/lib/api";
import type {
  MrpRfq,
  MrpRfqAttachmentAudience,
} from "@/lib/rfqApi";

type UploadTicketStatus =
  | "queued"
  | "active"
  | "completed"
  | "cancelled"
  | "expired"
  | "failed";

interface UploadTicket {
  id: string;
  status: UploadTicketStatus;
  error_message: string | null;
}

export interface RfqAttachmentUploadPermit {
  bucket: string;
  chunk_size_bytes: number;
  endpoint: string;
  object_path: string;
  token: string;
}

interface UploadTicketResponse {
  max_active_uploads: number;
  max_file_size_bytes: number;
  permit: RfqAttachmentUploadPermit | null;
  position: number | null;
  ticket: UploadTicket;
}

export interface RfqAttachmentUploadProgress {
  phase: "queued" | "uploading" | "finalizing";
  percent?: number;
  position?: number | null;
}

export type RfqAttachmentUploadTarget = {
  audience: MrpRfqAttachmentAudience;
  lineId: number | null;
};

const QUEUE_POLL_INTERVAL_MS = 1_000;
const HEARTBEAT_INTERVAL_MS = 30_000;
const ENGINEERING_DRAWING_EXTENSIONS = new Set([
  "3mf",
  "asm",
  "dwg",
  "dxf",
  "glb",
  "gltf",
  "iges",
  "igs",
  "obj",
  "prt",
  "sldasm",
  "sldprt",
  "step",
  "stl",
  "stp",
  "x_b",
  "x_t",
]);

function wait(milliseconds: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
}

function terminalTicketError(response: UploadTicketResponse): Error {
  return new Error(
    response.ticket.error_message ??
      `Attachment upload ${response.ticket.status}.`,
  );
}

export function rfqAttachmentUploadTargetKey(
  target: RfqAttachmentUploadTarget,
): string {
  return `${target.audience}:${target.lineId ?? "header"}`;
}

export function rfqAttachmentContentType(file: File): string {
  const extension = file.name.split(".").at(-1)?.toLowerCase() ?? "";
  if (ENGINEERING_DRAWING_EXTENSIONS.has(extension)) {
    return "application/octet-stream";
  }
  return file.type || "application/octet-stream";
}

export function formatRfqAttachmentUploadError(error: unknown): string {
  const raw = error instanceof Error ? error.message : String(error);
  const compactJws = /invalid compact jws/i.test(raw);
  if (compactJws) {
    return "Could not upload the file. Storage rejected the upload token. Try again.";
  }
  const tusMatch = /unexpected response while creating upload[\s\S]*response text:\s*(\{[\s\S]*\})/i.exec(
    raw,
  );
  if (tusMatch?.[1]) {
    try {
      const parsed = JSON.parse(tusMatch[1]) as { message?: unknown };
      if (typeof parsed.message === "string" && parsed.message.trim() !== "") {
        return `Could not upload the file. ${parsed.message.trim()}`;
      }
    } catch {
      /* keep a short fallback below */
    }
  }
  if (raw.length > 220) {
    return "Could not upload the file. Try again, or use a smaller file.";
  }
  return raw.trim() !== "" ? raw : "Could not upload the file.";
}

async function waitForUploadPermit(
  rfqId: number,
  initialResponse: UploadTicketResponse,
  onProgress: (progress: RfqAttachmentUploadProgress) => void,
): Promise<UploadTicketResponse> {
  let response = initialResponse;
  while (response.ticket.status === "queued") {
    onProgress({ phase: "queued", position: response.position });
    await wait(QUEUE_POLL_INTERVAL_MS);
    response = await apiFetch<UploadTicketResponse>(
      `/api/mrp/rfqs/${rfqId}/attachment-uploads/${response.ticket.id}`,
    );
  }
  if (response.ticket.status !== "active" || response.permit == null) {
    throw terminalTicketError(response);
  }
  return response;
}

function signedPutUrl(permit: RfqAttachmentUploadPermit): string {
  const url = new URL(permit.endpoint);
  if (!url.searchParams.get("token") && permit.token.trim() !== "") {
    url.searchParams.set("token", permit.token);
  }
  return url.toString();
}

function signedPutHeaders(file: File): Record<string, string> {
  return {
    "Content-Type": rfqAttachmentContentType(file),
    "x-upsert": "false",
  };
}

function rejectFailedStorageResponse(status: number, body: string): Error {
  const trimmed = body.trim();
  return new Error(
    trimmed !== ""
      ? `Storage upload failed (${status}): ${trimmed}`
      : `Storage upload failed (${status}).`,
  );
}

async function putSignedUploadWithFetch(
  file: File,
  permit: RfqAttachmentUploadPermit,
  onProgress: (progress: RfqAttachmentUploadProgress) => void,
): Promise<void> {
  onProgress({ phase: "uploading", percent: 0 });
  const response = await fetch(signedPutUrl(permit), {
    method: "PUT",
    headers: signedPutHeaders(file),
    body: file as unknown as BodyInit,
  });
  if (!response.ok) {
    throw rejectFailedStorageResponse(response.status, await response.text());
  }
  onProgress({ phase: "uploading", percent: 100 });
}

function putSignedUploadWithXhr(
  file: File,
  permit: RfqAttachmentUploadPermit,
  onProgress: (progress: RfqAttachmentUploadProgress) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open("PUT", signedPutUrl(permit));
    const headers = signedPutHeaders(file);
    for (const [name, value] of Object.entries(headers)) {
      request.setRequestHeader(name, value);
    }
    request.upload.onprogress = (event) => {
      if (!event.lengthComputable || event.total <= 0) return;
      onProgress({
        phase: "uploading",
        percent: Math.round((event.loaded / event.total) * 100),
      });
    };
    request.onload = () => {
      if (request.status >= 200 && request.status < 300) {
        resolve();
        return;
      }
      reject(rejectFailedStorageResponse(request.status, request.responseText));
    };
    request.onerror = () => {
      reject(new Error("Could not reach storage to upload the file."));
    };
    request.onabort = () => {
      reject(new Error("Attachment upload was cancelled."));
    };
    request.send(file);
  });
}

export function runRfqAttachmentSignedUpload(
  file: File,
  permit: RfqAttachmentUploadPermit,
  onProgress: (progress: RfqAttachmentUploadProgress) => void,
): Promise<void> {
  if (typeof XMLHttpRequest !== "function") {
    return putSignedUploadWithFetch(file, permit, onProgress);
  }
  return putSignedUploadWithXhr(file, permit, onProgress);
}

export async function uploadDirectMrpRfqAttachment(
  rfqId: number,
  file: File,
  options: {
    audience: MrpRfqAttachmentAudience;
    lineId: number | null;
    onProgress: (progress: RfqAttachmentUploadProgress) => void;
  },
): Promise<MrpRfq> {
  const initialResponse = await apiFetch<UploadTicketResponse>(
    `/api/mrp/rfqs/${rfqId}/attachment-uploads`,
    {
      method: "POST",
      body: {
        audience: options.audience,
        content_type: rfqAttachmentContentType(file),
        file_size_bytes: file.size,
        filename: file.name,
        line_id: options.lineId,
      },
    },
  );
  const ticketId = initialResponse.ticket.id;
  let heartbeatTimer: number | null = null;
  try {
    const activeResponse = await waitForUploadPermit(
      rfqId,
      initialResponse,
      options.onProgress,
    );
    heartbeatTimer = window.setInterval(() => {
      void apiFetch(
        `/api/mrp/rfqs/${rfqId}/attachment-uploads/${ticketId}/heartbeat`,
        { method: "POST" },
      ).catch(() => undefined);
    }, HEARTBEAT_INTERVAL_MS);
    await runRfqAttachmentSignedUpload(
      file,
      activeResponse.permit!,
      options.onProgress,
    );
    options.onProgress({ phase: "finalizing" });
    const finalized = await apiFetch<{ rfq: MrpRfq }>(
      `/api/mrp/rfqs/${rfqId}/attachment-uploads/${ticketId}/finalize`,
      { method: "POST" },
    );
    return finalized.rfq;
  } catch (error) {
    await apiFetch(
      `/api/mrp/rfqs/${rfqId}/attachment-uploads/${ticketId}`,
      { method: "DELETE" },
    ).catch(() => undefined);
    throw error;
  } finally {
    if (heartbeatTimer != null) {
      window.clearInterval(heartbeatTimer);
    }
  }
}
